/**
 * ReservationSummaryCards - 클릭 가능한 KPI 카드 4개
 * BL-129: 기간 선택 기능 포함, UI 개선
 * Phase 0D2: consults prop 제거 (summaryMap만 사용)
 */

import { Clock, Calendar, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { AppointmentDailySummary } from "@/api/appointment";
import dayjs from "dayjs";

interface SummaryCardDef {
  id: string;
  label: string;
  subLabel: string;
  icon: React.ElementType;
  iconColor: string;
  bgColor: string;
  borderColor: string;
  activeTextColor: string;
  getCount: (
    summaryMap: Map<string, AppointmentDailySummary>,
  ) => number;
  filterAction: {
    statusFilters?: string[];
    datePreset?: string;
    getDateRange?: () => { from: Date; to: Date };
  };
}

const CARDS: SummaryCardDef[] = [
  {
    id: "pending",
    label: "미처리 신청",
    subLabel: "승인 대기 중",
    icon: Clock,
    iconColor: "text-warning",
    bgColor: "bg-warning/10",
    borderColor: "border-warning/50",
    activeTextColor: "text-warning",
    getCount: (map) => {
      let count = 0;
      map.forEach((summary) => {
        count += summary.pendingCount || 0;
      });
      return count;
    },
    // 날짜는 현재 프리셋 유지, 상태만 필터링
    filterAction: {
      statusFilters: ["REQUESTED", "CHECKING"],
    },
  },
  {
    id: "today",
    label: "오늘 예약",
    subLabel: dayjs().format("M월 D일 기준"),
    icon: Calendar,
    iconColor: "text-info",
    bgColor: "bg-info/10",
    borderColor: "border-info/30",
    activeTextColor: "text-info",
    getCount: (map) => {
      const todayKey = dayjs().format("YYYY-MM-DD");
      return map.get(todayKey)?.totalCount || 0;
    },
    // 오늘 하루만 조회 (카운트 범위와 일치)
    filterAction: {
      getDateRange: () => ({
        from: dayjs().startOf("day").toDate(),
        to: dayjs().endOf("day").toDate(),
      }),
    },
  },
  {
    id: "confirmed",
    label: "최근 1주 확정",
    subLabel: "향후 7일 확정 예약",
    icon: CheckCircle2,
    iconColor: "text-emerald-500",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-300",
    activeTextColor: "text-emerald-700",
    getCount: (map) => {
      const now = dayjs();
      let count = 0;
      for (let i = 0; i <= 7; i++) {
        const key = now.add(i, "day").format("YYYY-MM-DD");
        count += map.get(key)?.confirmedCount || 0;
      }
      return count;
    },
    // 향후 7일 + CONFIRMED (카운트 범위와 일치)
    filterAction: {
      statusFilters: ["CONFIRMED"],
      getDateRange: () => ({
        from: dayjs().startOf("day").toDate(),
        to: dayjs().add(7, "day").endOf("day").toDate(),
      }),
    },
  },
  {
    id: "cancelled",
    label: "취소/노쇼",
    subLabel: "최근 30일",
    icon: AlertTriangle,
    iconColor: "text-destructive",
    bgColor: "bg-destructive/10",
    borderColor: "border-destructive/30",
    activeTextColor: "text-destructive",
    getCount: (map) => {
      const now = dayjs();
      let count = 0;
      for (let i = 0; i <= 30; i++) {
        const key = now.subtract(i, "day").format("YYYY-MM-DD");
        const summary = map.get(key);
        count += (summary?.cancelledCount || 0) + (summary?.noShowCount || 0);
      }
      return count;
    },
    // 과거 30일 조회 (카운트 범위와 일치)
    filterAction: {
      statusFilters: ["CANCELLED", "NO_SHOW"],
      getDateRange: () => ({
        from: dayjs().subtract(30, "day").startOf("day").toDate(),
        to: dayjs().endOf("day").toDate(),
      }),
    },
  },
];

interface ReservationSummaryCardsProps {
  summaryMap: Map<string, AppointmentDailySummary>;
  activeCardId?: string | null;
  onCardClick: (
    cardId: string,
    filterAction: SummaryCardDef["filterAction"],
  ) => void;
}

export function ReservationSummaryCards({
  summaryMap,
  activeCardId,
  onCardClick,
}: ReservationSummaryCardsProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {CARDS.map((card) => {
        const Icon = card.icon;
        const count = card.getCount(summaryMap);
        const isActive = activeCardId === card.id;

        return (
          <Card
            key={card.id}
            className={cn(
              "cursor-pointer transition-all hover:shadow-md border-2",
              isActive
                ? `${card.borderColor} ${card.bgColor} shadow-md ring-1 ring-offset-1`
                : "border-transparent hover:border-border/60",
            )}
            onClick={() => onCardClick(card.id, card.filterAction)}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span
                  className={cn(
                    "text-xs font-semibold",
                    isActive ? card.activeTextColor : "text-muted-foreground",
                  )}
                >
                  {card.label}
                </span>
                <div
                  className={cn(
                    "h-7 w-7 rounded-full flex items-center justify-center",
                    isActive ? card.bgColor : "bg-muted/50",
                  )}
                >
                  <Icon className={cn("h-4 w-4", card.iconColor)} />
                </div>
              </div>
              <div
                className={cn(
                  "text-2xl font-bold",
                  isActive && card.activeTextColor,
                )}
              >
                {count}
                <span className="text-sm font-normal ml-0.5 text-muted-foreground">
                  건
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                {card.subLabel}
                {isActive && (
                  <span className="ml-1 text-primary font-medium">
                    • 필터 적용 중
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export { CARDS };
export type { SummaryCardDef };
